import React, { useMemo, useState } from "react";
import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useListBestSkus, {
  BestSku,
  BestSkusPeriod,
  BestSkusSource,
} from "@/features/sku-dashboard/hooks/use-list-best-skus";
import DashboardCard from "@/features/dashboard/components/dashboard-card";
import DashboardCardSpinner from "@/features/dashboard/components/dashboard-card-spinner";
import BestSellingProductCard from "@/features/dashboard/features/best-selling-section/components/best-selling-product-card";
import AppSelect from "@/features/line-sheet-sets/app-select";

interface Props {
  onSelect: (sku: string) => void;
  // 지금 검색돼 있는 SKU — 목록에서 눈에 띄게 표시한다
  selectedSku?: string;
}

const COLLAPSED_COUNT = 10;
const ROW_HEIGHT = 236; // 카드 한 행의 대략 높이(px)

function BestSkuGrid({
  items,
  onSelect,
  selectedSku,
  isExpanded,
  isFetching,
}: {
  items: BestSku[];
  onSelect: (sku: string) => void;
  selectedSku?: string;
  isExpanded: boolean;
  isFetching: boolean;
}) {
  const { t } = useI18nHelper();
  return (
    <Box
      maxH={isExpanded ? `${ROW_HEIGHT * 2}px` : undefined}
      overflowY={isExpanded ? "auto" : "visible"}
      padding={"0 16px"}
      // 기간·소스를 바꾸는 동안 이전 목록을 흐리게 유지한다(깜빡임 없이 바뀐다)
      opacity={isFetching ? 0.45 : 1}
      transition={"opacity 0.15s"}
      pointerEvents={isFetching ? "none" : "auto"}
    >
      <Grid templateColumns={"repeat(10, 1fr)"} gap={"8px"}>
        {items.map((item) => {
          const isSelected = !!selectedSku && item.sku === selectedSku;
          return (
            <Box
              key={`${item.brand_id}-${item.sku}-${item.ranking}`}
              onClickCapture={(e) => {
                e.stopPropagation();
                onSelect(item.sku);
              }}
              cursor={"pointer"}
              minW={0}
              borderRadius={"6px"}
              boxShadow={isSelected ? "0 0 0 2px #1272EF" : undefined}
              background={isSelected ? "#F0F7FF" : undefined}
              title={`${item.brand_name} ${item.sku}`}
            >
              <BestSellingProductCard
                product={{ ...item, highlight: isSelected }}
                showOrderable={false}
              >
                <Flex flexDirection={"column"} gap={"4px"}>
                  <BestSellingProductCard.Image imgRef={null} />
                  <Flex flexDirection={"column"} gap={"2px"} color={"#444444"}>
                    <BestSellingProductCard.Name />
                    <BestSellingProductCard.Sku />
                    <BestSellingProductCard.Price />
                    {/* 순위의 근거가 되는 판매량 — 숫자가 있어야 1위와 10위의 차이가 보인다 */}
                    {typeof item.sale_count === "number" && (
                      <Flex justifyContent={"space-between"} alignItems={"baseline"}>
                        <Text textStyle={"caption-regular"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
                          {t("sales_volume")}
                        </Text>
                        <Text textStyle={"caption-bold"} color={"#1272EF"} whiteSpace={"nowrap"}>
                          {item.sale_count.toLocaleString()}
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </BestSellingProductCard>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
}

export default function BestSkuSection({ onSelect, selectedSku }: Props) {
  const { t, tTitle } = useI18nHelper();
  const [isExpanded, setIsExpanded] = useState(false);
  const [period, setPeriod] = useState<BestSkusPeriod>("1 month");
  const [source, setSource] = useState<BestSkusSource>("kream");
  const { bestSkus, isPending, isFetching, isError, refetch } = useListBestSkus(
    period,
    source
  );

  const periodOptions = useMemo<{ name: string; value: BestSkusPeriod }[]>(
    () => [
      { name: t("period.7days"), value: "7 days" },
      { name: t("period.1month"), value: "1 month" },
      { name: t("period.3month"), value: "3 months" },
      { name: t("period.6month"), value: "6 months" },
      { name: t("period.1year"), value: "1 year" },
    ],
    [t]
  );

  const sourceOptions = useMemo<{ name: string; value: BestSkusSource }[]>(
    () => [
      { name: t("market_source.kream"), value: "kream" },
      { name: t("market_source.poizon"), value: "poizon" },
    ],
    [t]
  );

  const items = isExpanded ? bestSkus : bestSkus.slice(0, COLLAPSED_COUNT);
  const canExpand = bestSkus.length > COLLAPSED_COUNT;

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"100%"}
        >
          <Flex alignItems={"baseline"} gap={"8px"}>
            <Text textStyle={"body-lg-bold"} color={"#444444"}>
              {tTitle("popular_skus")}
            </Text>
            {bestSkus.length > 0 && (
              <Text textStyle={"caption-regular"} color={"#8F8F8C"}>
                {t("best_skus_shown", {
                  shown: items.length,
                  total: bestSkus.length,
                })}
              </Text>
            )}
          </Flex>
          <Flex gap={"8px"} alignItems={"center"}>
            <Box width={"110px"}>
              <AppSelect
                name={"소스 선택"}
                options={sourceOptions}
                value={source}
                onSelect={(v) => setSource(v as BestSkusSource)}
                isClearable={false}
                width={"110px"}
              />
            </Box>
            {source === "kream" ? (
              <Box width={"140px"}>
                <AppSelect
                  name={"기간 선택"}
                  options={periodOptions}
                  value={period}
                  onSelect={(v) => setPeriod(v as BestSkusPeriod)}
                  isClearable={false}
                  width={"140px"}
                />
              </Box>
            ) : (
              // 포이즌 판매량은 30일 롤링 관측값이라 기간을 못 고른다
              <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                {t("period.30days_fixed")}
              </Text>
            )}
          </Flex>
        </Flex>
      </DashboardCard.Header>
      <DashboardCard.Body>
        {isPending ? (
          <Box height={`${ROW_HEIGHT}px`}>
            <DashboardCardSpinner />
          </Box>
        ) : isError ? (
          <Flex
            height={"120px"}
            alignItems={"center"}
            justifyContent={"center"}
            flexDirection={"column"}
            gap={"8px"}
          >
            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {t("best_skus_load_failed")}
            </Text>
            <Flex
              as={"button"}
              type={"button"}
              alignItems={"center"}
              gap={"4px"}
              padding={"4px 12px"}
              borderRadius={"6px"}
              border={"1px solid #D4D4D1"}
              color={"#444444"}
              fontSize={"12px"}
              onClick={() => refetch()}
            >
              <SVG src={"/icons-v1_1/arrow-rotate.svg"} width={12} height={12} />
              <Text>{t("retry")}</Text>
            </Flex>
          </Flex>
        ) : bestSkus.length === 0 ? (
          <Flex height={"120px"} alignItems={"center"} justifyContent={"center"}>
            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {t("best_skus_empty")}
            </Text>
          </Flex>
        ) : (
          <BestSkuGrid
            items={items}
            onSelect={onSelect}
            selectedSku={selectedSku}
            isExpanded={isExpanded}
            isFetching={isFetching}
          />
        )}
        {canExpand && (
          <Flex justifyContent={"center"} padding={"12px 0 4px"}>
            <Flex
              as={"button"}
              type={"button"}
              alignItems={"center"}
              gap={"6px"}
              padding={"6px 16px"}
              borderRadius={"8px"}
              border={"1px solid #E5E5E5"}
              backgroundColor={"#FFFFFF"}
              color={"#666666"}
              fontSize={"12px"}
              fontWeight={500}
              cursor={"pointer"}
              transition={"all 0.15s"}
              _hover={{
                backgroundColor: "#F7F7F5",
                borderColor: "#D0D0D0",
                color: "#444444",
              }}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              <Text>
                {isExpanded
                  ? t("collapse")
                  : `${t("expand")} (${bestSkus.length - COLLAPSED_COUNT})`}
              </Text>
              <Box
                transition={"transform 0.2s"}
                transform={isExpanded ? "rotate(180deg)" : "rotate(0deg)"}
                display={"flex"}
              >
                <SVG
                  src={"/icons-v1_1/chevron-down.svg"}
                  width={14}
                  height={14}
                />
              </Box>
            </Flex>
          </Flex>
        )}
      </DashboardCard.Body>
    </DashboardCard>
  );
}
